<script setup lang="ts">
import { useSessionStorage } from '@vueuse/core';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { AuthViaStorageKey } from '~/enums/auth.enum';

const { acceptInvitation } = useB2BInvitation();
const { getUser } = useAuthentication();
const { dispatchMessage } = useSystemMessages();

const props = defineProps<{
  notificationId: string;
  groupName: string;
  inviterName: string;
  invitationLink: string;
}>();

const emit = defineEmits<{
  (e: 'accepted'): void;
}>();

const isVisible = defineModel<boolean>('visible');
const isAccepting = ref(false);

const inviterInitials = computed(() => {
  if (!props.inviterName) {
    return '?';
  }

  const names = props.inviterName.trim().split(' ').filter(n => n);

  if (names.length === 0) {
    return '?';
  }

  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }

  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
});

const closeDialog = () => {
  isVisible.value = false;
};

const handleAccept = async () => {
  isAccepting.value = true;

  try {
    await acceptInvitation(props.invitationLink);
    await getUser();
    emit('accepted');
  } catch (error) {
    console.error(error);
    dispatchMessage('Error', 'error', 'Failed to accept invitation');
  } finally {
    isAccepting.value = false;
  }
};

const list = [
  'Full access to all our courses and workshops',
  'Download all our handy factsheets and books',
  'Claim CME credits (if your group has a Pro license)',
  'Weekly newsletter with useful tips, inspirations, and downloads',
];
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :close-on-escape="false"
    :modal="true"
    :show-header="false"
    :pt="{
      root: {
        class: 'relative flex items-center justify-center w-auto mx-auto h-auto !rounded-3xl sm:!rounded-2xl'
      },
      content: {
        class: 'rounded-xl !p-0'
      }
    }"
    @update:visible="closeDialog()"
  >
    <div class="w-full sm:max-w-[80vw] sm:w-[436px] relative p-8">
      <div class="flex flex-col gap-4">
        <div>
          <div class="flex justify-center">
            <div
              :class="[
                'w-[86px] h-[86px] rounded-full',
                'bg-neutral bg-neutral-5 border border-neutral-20',
                'text-36 font-bold text-neutral-80',
                'flex items-center justify-center',
                'shadow-medmastery'
              ]"
            >
              {{inviterInitials}}
            </div>
          </div>

          <div class="flex justify-center mt-3">
            <div class="text-center px-2 py-1 bg-neutral-5 rounded-lg text-neutral-80 text-14 font-bold border border-neutral-20">
              Invite from {{inviterName}}
            </div>
          </div>

          <div class="flex flex-col gap-2 text-center mt-4">
            <div class="text-28 font-bold text-neutral-100">
              You have been invited to the group
            </div>

            <div class="text-16 text-neutral-80 font-bold">
              {{groupName}}
            </div>
          </div>
        </div>

        <Divider class="my-4" />

        <div class="flex flex-col gap-4">
          <div
            v-for="(item, index) in list"
            :key="index"
            class="flex items-center gap-2 text-neutral-80"
          >
            <FontAwesomeIcon
              icon="fa-solid fa-circle-check"
              class="text-24 left-4 text-success"
            />
            <span class="text-14 font-bold">{{item}}</span>
          </div>
        </div>

        <div class="flex justify-end gap-4 mt-4">
          <Button
            rounded
            text
            severity="secondary"
            label="Not now"
            :disabled="isAccepting"
            @click="closeDialog()"
          />

          <Button
            rounded
            severity="primary"
            label="Accept invitation"
            :loading="isAccepting"
            @click="handleAccept"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
